import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Package } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { VAT_RATES } from "@/lib/vatRates";

interface AddonForm {
  name: string;
  description: string;
  price: number;
  is_active: boolean;
  sort_order: number;
  available_from: string;
  available_to: string;
  available_days: number[];
  vat_rate: number;
}

const emptyForm: AddonForm = { name: "", description: "", price: 0, is_active: true, sort_order: 0, available_from: "", available_to: "", available_days: [1, 2, 3, 4, 5, 6, 7], vat_rate: 25 };

export default function AdminAddons() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AddonForm>(emptyForm);

  const { data: addons = [], isLoading: _isLoading } = useQuery({
    queryKey: ["admin-addons", organization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_addons" as any)
        .select("*")
        .eq("organization_id", organization!.id)
        .order("sort_order");
      if (error) throw error;
      return data as any[];
    },
    enabled: !!organization?.id,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        organization_id: organization!.id,
        name: form.name,
        description: form.description || null,
        price: form.price,
        is_active: form.is_active,
        sort_order: form.sort_order,
        available_from: form.available_from || null,
        available_to: form.available_to || null,
        available_days: form.available_days,
        vat_rate: form.vat_rate,
      };
      if (editingId) {
        const { error } = await supabase
          .from("booking_addons" as any)
          .update(payload)
          .eq("id", editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("booking_addons" as any)
          .insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-addons"] });
      setDialogOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      toast.success(editingId ? "Tillägg uppdaterat" : "Tillägg skapat");
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte spara tillägget"); },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("booking_addons" as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-addons"] });
      toast.success(t("admin:toast.addonDeleted"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || "Kunde inte ta bort tillägget"); },
  });

  const openEdit = (addon: any) => {
    setEditingId(addon.id);
    setForm({
      name: addon.name,
      description: addon.description ?? "",
      price: addon.price,
      is_active: addon.is_active,
      sort_order: addon.sort_order,
      available_from: addon.available_from?.slice(0, 5) ?? "",
      available_to: addon.available_to?.slice(0, 5) ?? "",
      available_days: addon.available_days ?? [1, 2, 3, 4, 5, 6, 7],
      vat_rate: addon.vat_rate ?? 25,
    });
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: addons.length });
    setDialogOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-lg flex items-center gap-2">
          <Package className="h-5 w-5" /> Bokningstillägg
        </CardTitle>
        <Button size="sm" onClick={openNew} className="gap-1.5">
          <Plus className="h-4 w-4" /> Nytt tillägg
        </Button>
      </CardHeader>
      <CardContent>
        {addons.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            Inga tillägg skapade ännu. Klicka "Nytt tillägg" för att komma igång.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">#</TableHead>
                <TableHead>Namn</TableHead>
                <TableHead>Pris</TableHead>
                <TableHead>Aktiv</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {addons.map((addon: any) => (
                <TableRow key={addon.id}>
                  <TableCell className="text-muted-foreground">{addon.sort_order}</TableCell>
                  <TableCell>
                    <div>
                      <span className="font-medium">{addon.name}</span>
                      {addon.description && (
                        <p className="text-xs text-muted-foreground">{addon.description}</p>
                      )}
                      {addon.available_from && (
                        <span className="text-[10px] text-muted-foreground">
                          {addon.available_from?.slice(0, 5)}–{addon.available_to?.slice(0, 5)}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{addon.price} kr</TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium ${addon.is_active ? "text-green-600" : "text-muted-foreground"}`}>
                      {addon.is_active ? "Ja" : "Nej"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(addon)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => deleteMutation.mutate(addon.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingId ? "Redigera tillägg" : "Nytt tillägg"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Namn</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="t.ex. Bollar (3-pack)" />
            </div>
            <div className="space-y-1.5">
              <Label>Beskrivning</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Valfritt" rows={2} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Pris (kr)</Label>
                <NumberInput min={0} value={form.price} onValueChange={(v) => setForm({ ...form, price: v })} />
              </div>
              <div className="space-y-1.5">
                <Label>Moms</Label>
                <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: Number(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {VAT_RATES.map((r) => (
                      <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sortering</Label>
                <NumberInput min={0} value={form.sort_order} onValueChange={(v) => setForm({ ...form, sort_order: v })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Aktiv</Label>
            </div>

            <div className="space-y-3 border-t pt-3">
              <Label className="text-xs font-medium">Tillgänglighet</Label>
              <p className="text-[10px] text-muted-foreground -mt-1">
                Lämna tomt för att visa vid alla tider. Sätt tider om tillägget kräver bemannad shop.
              </p>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label className="text-xs">Tillgänglig från</Label>
                  <Input
                    type="time"
                    value={form.available_from}
                    onChange={(e) => setForm({ ...form, available_from: e.target.value })}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tillgänglig till</Label>
                  <Input
                    type="time"
                    value={form.available_to}
                    onChange={(e) => setForm({ ...form, available_to: e.target.value })}
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Dagar</Label>
                <div className="flex gap-1">
                  {["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"].map((label, i) => {
                    const dayNum = i + 1;
                    return (
                      <button
                        key={dayNum}
                        type="button"
                        className={cn(
                          "h-7 w-9 rounded text-xs font-medium transition-colors",
                          form.available_days.includes(dayNum)
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground hover:bg-muted/80"
                        )}
                        onClick={() => {
                          const next = form.available_days.includes(dayNum)
                            ? form.available_days.filter((d) => d !== dayNum)
                            : [...form.available_days, dayNum].sort();
                          if (next.length > 0) setForm({ ...form, available_days: next });
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Avbryt</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!form.name || saveMutation.isPending}>
              {editingId ? "Uppdatera" : "Skapa"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
